import {
  createContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
  FC,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useMatch } from 'react-router-dom';
import { notifyError, notifySuccess } from '../helpers/utils';
import { addSourceFileInSession, fetchSourceFiles, getMappingSession } from '../services/apiCalls';
import { MappingSessionResponse } from '../types/types';
import _ from 'lodash';

import {
  IGeneralizationContext,
  GeneralizationSuggestion,
  GeneralizationDocument,
  AIResponse,
  GeneralizationSuggestionMap,
  MappingTypeEnum,
  ICategory,
  IGroupingVariables,
} from '../types';
import {
  gvCategoriesArray,
  clearHighlightedElements,
  fixTablesFormatting,
  getAllCells,
  deepCopyDocument,
  processImageSuggestions,
  escapeHtml,
  escapeRegex,
} from '../helpers/generalizationHelpers';
import { CatalogContext } from './CatalogContext';
import { MappingContext } from './MappingContext';
import { Placeholder } from '../services/api/types';
import { FeatureFlag, featureFlagStore } from '../stores';
import { ISqlQueryObj } from '../pages/DataSource/types';

export const defaultGeneralizationContext: IGeneralizationContext = {
  setImageSuggestions: () => undefined,
  imageSuggestions: {},
  setImageSuggestionsFiles: () => undefined,
  imageSuggestionsFiles: [],
  gvCategories: [],
  setGVCategories: () => undefined,
  acceptedSuggestions: [],
  aiResponse: undefined,
  generatedDocument: undefined,
  sourceDocuments: undefined,
  selectedCatalogId: -1,
  setLoadingSourceDocument: () => undefined,
  loadingSourceDocument: false,
  setSelectedCatalogId: () => undefined,
  setAcceptedSuggestions: () => undefined,
  loadingGeneratedDocument: false,
  setGeneratedDocumentWithAcceptedSuggestions: (_doc?: Document, _setGenDoc?: boolean) => undefined,
  sessionId: undefined,
  getSourceNodeIndex: (
    _targetNodeId: string,
    _aiRes?: AIResponse | undefined,
    _sourceNodeId?: string | null,
  ): number => {
    return 0;
  },
  setAiResponse: () => undefined,
  setSourceDocuments: () => undefined,
  handleSourcechange: () => undefined,
  handleAddSourceFiles: () => undefined,
  selectedValue: '',
  setSelectedValue: () => undefined,
  openSourceModal: () => undefined,
  closeSourceModal: () => undefined,
  sourceModalOpen: false,
  setSourceModalOpen: () => undefined,
  handleGetSourceContent: (_id: number) => undefined,
  lastSaved: new Date(),
  setLastSaved: () => undefined,
  authors: [],
  validators: [],
  setValidators: () => undefined,
  mappingType: MappingTypeEnum.AUTHORING,
  mappingSessionTitle: '',
  selectionSearch: true,
  setSelectionSearch: () => undefined,
  searchTerm: '',
  setSearchTerm: () => undefined,
  clearAISuggestions: false,
  setClearAISuggestions: () => undefined,
  aiResponseCopy: undefined,
  setAiResponseCopy: () => undefined,
  selectedSourceFileName: '',
  setSelectedSourceFileName: () => undefined,
  cells: [],
  setCells: () => undefined,
  lockSourceFile: false,
  setLockSourceFile: () => undefined,
  targetNodeIdSuggestionMap: {},
  sourceNodeIdSuggestionMap: {},
  isParsed: true,
  setGroupingVariables: () => undefined,
  groupingVariables: [],
  resetStates: () => undefined,
  isOriginalAvailable: false,
  sanitizeAiResponse: () => ({}) as AIResponse,
  isFullScreen: false,
  setIsFullScreen: () => undefined,
  isAutoSave: false,
  setIsAutoSave: () => undefined,
  initialTarget: undefined,
  initialTargetDocument: undefined,
  setInitialTarget: () => undefined,
  sessionStatus: '',
  setSessionStatus: () => undefined,
  selectedSourceDoc: undefined,
  baseTargetFileId: undefined,
};

export const GeneralizationContext = createContext<IGeneralizationContext>(
  defaultGeneralizationContext,
);

interface Props {
  children: ReactNode;
}

const GeneralizationContextContainer: FC<Props> = ({ children }) => {
  const [loadingGeneratedDocument, setLoadingGeneratedDocument] = useState<boolean>(
    defaultGeneralizationContext.loadingGeneratedDocument,
  );
  const [loadingSourceDocument, setLoadingSourceDocument] = useState<boolean>(
    defaultGeneralizationContext.loadingGeneratedDocument,
  );
  const [sessionId, setSessionId] = useState<number | undefined>();
  const [acceptedSuggestions, setAcceptedSuggestions] = useState<GeneralizationSuggestion[]>();
  const [aiResponse, setAiResponse] = useState<AIResponse | undefined>();
  const [initialTarget, setInitialTarget] = useState<Document>();
  const [initialTargetDocument, setInitialTargetDocument] = useState<GeneralizationDocument>();
  const [generatedDocument, setGeneratedDocument] = useState<GeneralizationDocument>();
  const [sourceDocuments, setSourceDocuments] = useState<Map<string, GeneralizationDocument>>();
  const [imageSuggestions, setImageSuggestions] = useState<{
    [key: number]: GeneralizationSuggestion[];
  }>({});
  const [imageSuggestionsFiles, setImageSuggestionsFiles] = useState<GeneralizationDocument[]>([]);
  const [selectedCatalogId, _setSelectedCatalogId] = useState<number>(0);
  const [lastSaved, setLastSaved] = useState<Date>();
  const [authors, setAuthors] = useState<string[]>([]);
  const [sessionStatus, setSessionStatus] = useState('');
  const [validators, setValidators] = useState<string[]>([]);
  const [mappingType, setMappingType] = useState(MappingTypeEnum.AI_AUTHORING);
  const [mappingSessionTitle, setMappingSessionTitle] = useState<string>('');
  const [selectionSearch, setSelectionSearch] = useState(false);
  const [lockSourceFile, setLockSourceFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clearAISuggestions, setClearAISuggestions] = useState(false);
  const [aiResponseCopy, setAiResponseCopy] = useState<AIResponse | undefined>();
  const [selectedSourceFileName, setSelectedSourceFileName] = useState<string>('');
  const [groupingVariables, setGroupingVariables] = useState<string[]>([]);
  const [selectedSourceDoc, setSelectedSourceDoc] = useState<GeneralizationDocument>();
  const [gvCategories, setGVCategories] = useState<ICategory[]>([]);
  const [isTargetInitialized, setIsTargetInitialized] = useState(false);
  const [isParsed, setIsParsed] = useState(defaultGeneralizationContext.isParsed);
  const [isFullScreen, setIsFullScreen] = useState(defaultGeneralizationContext.isFullScreen);
  const [isAutoSave, setIsAutoSave] = useState(defaultGeneralizationContext.isAutoSave);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [mappingSessionData, setMappingSessionData] = useState<MappingSessionResponse | undefined>(
    undefined,
  );
  const [baseTargetFileId, setBaseTargetFileId] = useState(undefined);

  const [cells, setCells] = useState<string[]>([]);
  const match = useMatch('/mapping-sessions/:id/*');
  const { selectedSourceDocuments, setSelectedSourceDocuments } = useContext(CatalogContext);
  const { setSourceFileDoc, editor1Ref, setTargetId } = useContext(MappingContext);
  const [selectedValue, setSelectedValue] = useState('');
  const [sourceModalOpen, setSourceModalOpen] = useState(false);

  const resetStates = useCallback(() => {
    setAcceptedSuggestions(defaultGeneralizationContext.acceptedSuggestions);
    setAiResponse(defaultGeneralizationContext.aiResponse);
    setGeneratedDocument(defaultGeneralizationContext.generatedDocument);
    setInitialTargetDocument(defaultGeneralizationContext.initialTargetDocument);
    setMappingSessionData(undefined);
    setSourceDocuments(defaultGeneralizationContext.sourceDocuments);
    setIsTargetInitialized(false);
    setSourceFileDoc({});
    setSessionStatus('');
  }, [setSourceFileDoc]);

  const sanitizeAiResponse = useCallback((aiResp: Record<string, unknown>): AIResponse => {
    const sanitizedResponse: Record<string, unknown> = {};

    for (const key in aiResp) {
      const sanitizedKey = key.replace(/\.html\[0\]\.body\[1\]/g, '');
      const value = aiResp[key];

      if (Array.isArray(value)) {
        sanitizedResponse[sanitizedKey] = value.map((item) =>
          typeof item === 'object' && item !== null
            ? sanitizeAiResponse(item as Record<string, unknown>)
            : item,
        );
      } else if (typeof value === 'object' && value !== null) {
        const entry = value as Record<string, unknown> & { value: string };
        sanitizedResponse[sanitizedKey] = {
          ...entry,
          value: _.unescape(String(entry.value ?? '')),
        };
      }
    }

    return sanitizedResponse as AIResponse;
  }, []);

  const handleGetSourceContent = useCallback(
    (currentSourceIds: number, getParsed: boolean | null = null) => {
      setLoadingSourceDocument(true);
      fetchSourceFiles([currentSourceIds], getParsed)
        .then(
          (res: {
            data: Array<{
              filename: string;
              html: string;
              id: number;
              isParsed: boolean;
              sourceDocNumber: string;
              version: number;
              dataSource: ISqlQueryObj;
            }>;
          }) => {
            setSelectedSourceDoc(res.data[0]);
            setIsParsed(res.data[0].isParsed);
          },
        )
        .catch((_err) => {
          notifyError('Error in fetching source content');
        })
        .finally(() => {
          setLoadingSourceDocument(false);
        });
    },
    [],
  );

  const { targetNodeIdSuggestionMap, sourceNodeIdSuggestionMap } = useMemo(() => {
    const targetNodeIdMap: GeneralizationSuggestionMap = {};
    const sourceNodeIdMap: GeneralizationSuggestionMap = {};

    acceptedSuggestions?.forEach((suggestion) => {
      targetNodeIdMap[suggestion.targetNodeId] = suggestion;
      if (suggestion.sourceNodeId) {
        sourceNodeIdMap[suggestion.sourceNodeId] = suggestion;
      }
    });

    return {
      targetNodeIdSuggestionMap: targetNodeIdMap,
      sourceNodeIdSuggestionMap: sourceNodeIdMap,
    };
  }, [acceptedSuggestions]);

  const getSourceNodeIndex = (
    targetNodeId: string,
    aiRes: AIResponse | undefined = aiResponse,
    sourceNodeId: string | null = null,
  ): number => {
    const acceptedSuggestion = targetNodeIdSuggestionMap[targetNodeId];
    if (!acceptedSuggestion) {
      return 0;
    }

    const sourceId = sourceNodeId ?? acceptedSuggestion.sourceNodeId;
    const index = _.findIndex(aiRes?.[targetNodeId], (o) => _.has(o, sourceId));
    return index;
  };

  const processHTML = (originalHtml: string) => {
    const filteredHtml = originalHtml.replaceAll('.html[0].body[1]', '');
    return fixTablesFormatting(filteredHtml);
  };

  useEffect(() => {
    match?.params.id && setSessionId(+match?.params.id);

    return () => setSessionId(undefined);
  }, [match]);

  const setGeneratedDocumentWithAcceptedSuggestions = useCallback(
    (
      documnet: Document | null = null,
      setGenDoc = true,
      suggestions = acceptedSuggestionsRef.current,
    ) => {
      if (!initialTargetDocument || suggestions == null) return;
      const parser = new DOMParser();

      let baseHtml = initialTargetDocument.html;
      if (mappingType === MappingTypeEnum.AI_AUTHORING) {
        baseHtml = clearHighlightedElements(baseHtml);
      }

      // Automated sessions load authoritative template HTML from the API. Cloned
      // acceptedSuggestions from the ground-truth session can still carry stale
      // targetValue text and overwrite the new template when merging from the
      // template alone (documnet == null). Skip that overlay on initial merge;
      // live editor / websocket paths pass documnet and still apply deltas.
      const shouldSkipSuggestionOverlay =
        mappingType === MappingTypeEnum.AUTO_MAPPING && documnet == null;

      const suggestionsToApply = shouldSkipSuggestionOverlay ? [] : suggestions;

      let workingHtml = baseHtml;

      suggestionsToApply.forEach((suggestion) => {
        const doc = documnet || parser.parseFromString(workingHtml, 'text/html');
        const generatedDocumentNode = doc.querySelector<HTMLElement>(
          `[data-nodeid='${
            suggestion.targetNodeId
          }'],[data-nodeid='${suggestion?.targetNodeId?.replace('.html[0].body[1]', '')}']`,
        );
        if (generatedDocumentNode) {
          if (generatedDocumentNode.tagName === 'IMG') {
            generatedDocumentNode.setAttribute('src', suggestion.targetValue);
            generatedDocumentNode.style.maxWidth = '500px';
            generatedDocumentNode.style.maxHeight = '300px';
            generatedDocumentNode.style.width = 'auto';
            generatedDocumentNode.style.height = 'auto';
          } else {
            generatedDocumentNode.innerHTML = suggestion.targetValue;
          }
          workingHtml = doc.body.outerHTML;
        }
      });
      if (workingHtml) {
        placeholders.forEach((value) => {
          const normalizedText = escapeHtml(value.oldValue);
          const regex = new RegExp(`${escapeRegex(normalizedText)}`, 'gi');

          workingHtml = workingHtml
            ?.normalize('NFKC')
            .replace(/[\u2010\u2013\u2014\u2212]/g, '-')
            .replace(regex, value.newValue)
            .replace(normalizedText, value.newValue);
        });
      }

      if (setGenDoc) setGeneratedDocument({ ...initialTargetDocument, html: workingHtml });
    },
    [initialTargetDocument, mappingType, placeholders],
  );

  const acceptedSuggestionsRef = useRef(acceptedSuggestions);
  useEffect(() => {
    acceptedSuggestionsRef.current = acceptedSuggestions;
  }, [acceptedSuggestions]);

  const setSourceDocumentWithAcceptedSuggestions = useCallback(() => {
    const suggestions = acceptedSuggestionsRef.current;
    if (!selectedSourceDoc || !aiResponse || !suggestions) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(selectedSourceDoc.html, 'text/html');
    suggestions.forEach((suggestion) => {
      const sourceFileDocNode = doc.querySelector(
        `[data-nodeid='${
          suggestion.sourceNodeId
        }'],[data-nodeid='${suggestion.sourceNodeId?.replace('.html[0].body[1]', '')}']`,
      );
      if (sourceFileDocNode && suggestion.sourceValue) {
        const tdElement = sourceFileDocNode.closest('td');
        const newNode = tdElement || sourceFileDocNode;
        newNode.setAttribute('data-nodeid', suggestion.sourceNodeId);
        newNode.innerHTML = suggestion.sourceValue;
        selectedSourceDoc.html = doc?.body?.innerHTML;
      }
    });
    setSourceFileDoc({ ...selectedSourceDoc });
  }, [aiResponse, selectedSourceDoc, setSourceFileDoc]);

  useEffect(() => {
    setSourceDocumentWithAcceptedSuggestions();
  }, [setSourceDocumentWithAcceptedSuggestions]);

  useEffect(() => {
    if (aiResponse && Object.keys(aiResponse).length && !isTargetInitialized) {
      setIsTargetInitialized(true);
      setGeneratedDocumentWithAcceptedSuggestions();
    } else {
      setGeneratedDocumentWithAcceptedSuggestions(
        isTargetInitialized ? (editor1Ref.current?.getDoc() ?? null) : null,
      );
    }
    const newTarget = editor1Ref.current?.getDoc();
    if (newTarget) setInitialTarget(deepCopyDocument(newTarget));
  }, [aiResponse, editor1Ref, isTargetInitialized, setGeneratedDocumentWithAcceptedSuggestions]);

  useEffect(() => {
    resetStates();
    if (sessionId) {
      let sourceId: number;
      setLoadingGeneratedDocument(true);
      getMappingSession(sessionId)
        .then((res) => {
          setMappingSessionData(res.data as unknown as MappingSessionResponse);
          const groupingVariables: IGroupingVariables = res.data.groupingVariables;
          setGVCategories(gvCategoriesArray(groupingVariables));
          setMappingSessionTitle(res.data.template.filename);
          setMappingType(res.data.mappingType);
          setPlaceholders(res.data.placeholders);
          sourceId = res.data.t2[0].id;
          const sanitizedAIResponse = sanitizeAiResponse(
            res.data.aiSuggestions as Record<string, unknown>,
          );
          const { sourceFiles, imageSuggestionsObject } = processImageSuggestions(res.data);
          setImageSuggestionsFiles(sourceFiles);
          setImageSuggestions(imageSuggestionsObject);
          setAiResponse(sanitizedAIResponse);
          setAiResponseCopy(sanitizedAIResponse);
          setAcceptedSuggestions(
            res.data.acceptedSuggestions.map((s: GeneralizationSuggestion) => ({
              ...s,
              sourceFileId: s.sourceFile?.id,
            })),
          );
          setInitialTargetDocument({
            ...res.data.template,
            html: processHTML(res.data.template.html),
          });
          setSourceDocuments(
            res.data.t2.reduce(
              (accu: Map<string, GeneralizationDocument>, source: GeneralizationDocument) => {
                accu.set(`${source.id}`, {
                  id: source.id,
                  html: source.html,
                  filename: source.filename,
                  nonParsedCatalogId: source.nonParsedCatalogId,
                  version: source.version,
                  dataSource: source.dataSource,
                  veevaNumber: source.veevaNumber,
                });
                return accu;
              },
              new Map(),
            ),
          );
          _setSelectedCatalogId(sourceId);
          setSelectedValue(String(sourceId));
          if (!featureFlagStore.isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
            handleGetSourceContent(sourceId);
          }
          setLastSaved(res.data.lastUpdatedAt ? new Date(res.data.lastUpdatedAt) : new Date());
          setAuthors(res.data.authors || []);
          setSessionStatus(res.data.status);
          setValidators(res.data.validators || []);
          setGroupingVariables(res.data.groupingVariables || []);
          setTargetId(res.data.template.id);
          setBaseTargetFileId(res.data.baseTargetFileId);
        })
        .catch((err) => {
          console.error(err);
          notifyError('Documents Not Found');
        })
        .finally(() => setLoadingGeneratedDocument(false));
    }
  }, [sessionId, resetStates, sanitizeAiResponse, setTargetId, handleGetSourceContent]);

  useEffect(() => {
    setCells(getAllCells(initialTargetDocument?.html || ''));
  }, [initialTargetDocument]);

  const openSourceModal = () => {
    setSelectedSourceDocuments([]);
    setSourceModalOpen(true);
  };
  const closeSourceModal = () => {
    setSelectedSourceDocuments([]);
    setSourceModalOpen(false);
  };

  const handleAddSourceFiles = async () => {
    if (sourceDocuments) {
      const sourceDocumentsKeys = Array.from(sourceDocuments.keys()).map(Number);
      const selectedSourceIds = selectedSourceDocuments.filter(
        (element: number) => !sourceDocumentsKeys.includes(element),
      );
      addSourceFileInSession(selectedSourceIds, sessionId)
        .then((res) => {
          const newData = res.data.reduce(
            (accu: Map<string, GeneralizationDocument>, sourceFile: GeneralizationDocument) => {
              accu.set(`${sourceFile.id}`, {
                id: sourceFile.id,
                html: sourceFile.html,
                filename: sourceFile.filename,
                nonParsedCatalogId: sourceFile.nonParsedCatalogId,
                version: sourceFile.version,
              });
              return accu;
            },
            new Map([...sourceDocuments]),
          );

          setSourceDocuments(newData);
          notifySuccess('Source files added successfully!');
          closeSourceModal();
        })
        .catch((err) => {
          console.error(err);
          notifyError('something went wrong!');
        });
    }
  };

  const setSelectedCatalogId = (catalogId: number) => {
    if (!sourceDocuments) return;

    if (sourceDocuments.get(`${catalogId}`)) {
      _setSelectedCatalogId(+catalogId);
    } else {
      const x = Array.from(sourceDocuments.values()).find(
        (value) => value.nonParsedCatalogId === catalogId,
      )?.id;
      if (!x) return;
      _setSelectedCatalogId(x);
    }
  };

  const handleSourcechange = useCallback(
    (value: string, getParsed: boolean | null = null) => {
      +value && handleGetSourceContent(+value, getParsed);
      _setSelectedCatalogId(+value);
      setSelectedValue(value);
    },
    [handleGetSourceContent],
  );

  const nonParsedCatalogIdExistForSelectedCatalog = useMemo(() => {
    const catalogInfo = sourceDocuments?.get(`${selectedCatalogId}`);
    if (!catalogInfo) return true;
    return !!catalogInfo?.nonParsedCatalogId;
  }, [selectedCatalogId, sourceDocuments]);

  const isOriginalAvailable =
    nonParsedCatalogIdExistForSelectedCatalog && !loadingGeneratedDocument;

  return (
    <GeneralizationContext.Provider
      value={{
        loadingGeneratedDocument,
        acceptedSuggestions,
        aiResponse,
        generatedDocument,
        sourceDocuments,
        setAcceptedSuggestions,
        getSourceNodeIndex,
        setAiResponse,
        sessionId,
        setSourceDocuments,
        handleSourcechange,
        handleAddSourceFiles,
        selectedValue,
        setSelectedValue,
        openSourceModal,
        closeSourceModal,
        sourceModalOpen,
        setSourceModalOpen,
        handleGetSourceContent,
        selectedCatalogId,
        setSelectedCatalogId,
        setLoadingSourceDocument,
        loadingSourceDocument,
        setGeneratedDocumentWithAcceptedSuggestions,
        lastSaved,
        setLastSaved,
        authors,
        validators,
        setValidators,
        mappingType,
        mappingSessionTitle,
        searchTerm,
        setSearchTerm,
        selectionSearch,
        setSelectionSearch,
        aiResponseCopy,
        setAiResponseCopy,
        clearAISuggestions,
        setClearAISuggestions,
        selectedSourceFileName,
        setSelectedSourceFileName,
        cells,
        setCells,
        lockSourceFile,
        setLockSourceFile,
        targetNodeIdSuggestionMap,
        sourceNodeIdSuggestionMap,
        isParsed,
        setGroupingVariables,
        groupingVariables,
        gvCategories,
        setGVCategories,
        resetStates,
        isOriginalAvailable,
        sanitizeAiResponse,
        isFullScreen,
        setIsFullScreen,
        setIsAutoSave,
        isAutoSave,
        initialTarget,
        initialTargetDocument,
        setInitialTarget,
        imageSuggestions,
        imageSuggestionsFiles,
        setImageSuggestions,
        setImageSuggestionsFiles,
        sessionStatus,
        setSessionStatus,
        mappingSessionData,
        selectedSourceDoc,
        baseTargetFileId,
      }}>
      {children}
    </GeneralizationContext.Provider>
  );
};

export default GeneralizationContextContainer;
